import * as ng from "@angular/core";
import * as Rx from "rxjs";
import { 
  AngularFirestore,
  QuerySnapshot,
  Query,
  DocumentData,
} from "@angular/fire/firestore";
import {
  Follow,
  Live,
  ArchiveUploadLive,
  CreatorComment,
  LiveCommentCount,
  LiveReactionCount,
  MagazineNgWord
} from "./entity/live";
import { Debugger, DebuggerList } from "./entity/debugger-list";
import { map } from "rxjs/operators";
import { OrderByDirection }  from "@firebase/firestore";
import { Config } from "./entity/config";
import { OrderMeta } from "./entity/order";
import { Gift } from "./entity/gift";
import { PUBLIC_STATUS } from "./constants";
import { MagliveUser } from "./entity/user"
import { Room } from "./entity/room";

export const ARCHIVE_PAGE_SIZE = 20
export const INFINITE_SCROLL_PAGE_SIZE = 2

@ng.Injectable()
export class FirestoreAccess {

  constructor(
    private afs: AngularFirestore
  ) {
  }

  getDebuggerList(): Rx.Observable<DebuggerList> {
    return this.afs.collection<Debugger>('debuggers').valueChanges().pipe(map(x => new DebuggerList(x)))
  }

  archivedLives(userId: number | null, magazineId: number | null, order?: string, start?: Date, end?: Date, last?: Live): Rx.Observable<Live[]> {
    order = order || '-startedAt'
    let orderDirection: OrderByDirection = 'asc'
    if (order.charAt(0) === '-'){
      order = order.substring(1)
      orderDirection = 'desc'
    }
    start = start || new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 30)
    return Rx.combineLatest([this.afs.collection<Live>("lives", ref => {
      let query = (magazineId === null ? ref : ref.where('magazineId', '==', magazineId))
      if (start) query = query.where('startedAt', '>=', start)
      if (end) query = query.where('startedAt', '<', end)
      query = query.orderBy(order, orderDirection).limit(ARCHIVE_PAGE_SIZE)
      return last ? query.startAfter(last[order]) : query
    })
    .valueChanges(), this.getDebuggerList()]).pipe(
      map(([lives, debuggerList]) => 
        lives
        .filter(live => debuggerList.canAccess(userId, live.magazineId, live.userId) && live.status === 'CLOSED')
      )
    )
  }

  broadcastingLives(userId: number | null, magazineId: number | null): Rx.Observable<Live[]> {
    return Rx.combineLatest([this.afs.collection<Live>("lives", ref =>
      (magazineId === null ? ref : ref.where('magazineId', '==', magazineId))
        .where('alive', '==', true) // alive == true で絞らないと全件クエリの対象に含まれてしまう
        .orderBy('createdAt', 'desc')
    ).valueChanges(), this.getDebuggerList()]).pipe(
      map(([lives, debuggerList]) =>
        lives.filter(live => debuggerList.canAccess(userId, live.magazineId, live.userId) && (live.status === 'ON_AIR' || (live.status === 'NO_STREAM' && live.startedAt !== null))))
    )
  }

  reservedLives(userId: number | null, magazineId: number | null): Rx.Observable<Live[]> {
    return Rx.combineLatest([this.afs.collection<Live>("lives", ref =>
      (magazineId === null ? ref : ref.where('magazineId', '==', magazineId))
        .where('status', '==', 'RESERVED') // status で絞らないと古い予約が全件クエリの対象になってしまう
        .orderBy('scheduledAt', 'desc')
    ).valueChanges(), this.getDebuggerList()]).pipe(
      map(([lives, debuggerList]) =>
        lives.filter(live => debuggerList.canAccess(userId, live.magazineId, live.userId)))
    )
  }

  follows(userId: number, magazineId: number): Rx.Observable<Follow[]> {
    return this.afs.collection<Follow>('follows', ref =>
      ref.where('userId', '==', userId)
        .where('magazineId', '==', magazineId)
    ).valueChanges()
  }

  getCreatorComment(liveId: string): Rx.Observable<CreatorComment[]> {
    return this.afs.collection<CreatorComment>(`lives/${liveId}/creator_comments`, ref => 
      ref.orderBy('createdAt', 'desc').limit(1)
    ).valueChanges();
  }

  getConfig(): Rx.Observable<Config[]> {
    return this.afs.collection<Config>(`config`).valueChanges();
  }

  getCommentCount(liveId: string): Rx.Observable<LiveCommentCount> {
    return this.afs.collection<LiveCommentCount>(`lives/${liveId}/comment_count`
      ).valueChanges().pipe(
        map(x => x.reduce((previous, current) => (
          { count: previous.count + current.count }), {count: 0} ))
      );
  }

  getReactionGoodCount(liveId: string): Rx.Observable<LiveReactionCount> {
    return this.afs.collection<LiveReactionCount>(`lives/${liveId}/reaction_count`, ref =>
      ref.where("kind", "==", "GOOD")
    ).valueChanges().pipe(
      map(x => x.reduce((previous, current) => (
        { count: previous.count + current.count, kind: "GOOD"}), {count: 0, kind: "GOOD"} ))
    );
  }

  getOrderStatus(orderId: string): Rx.Observable<OrderMeta[]> {
    return this.afs.collection<OrderMeta>(`orders/${orderId}/order_meta`).valueChanges();
  }


  getRoom(roomId: string): Rx.Observable<Room> {
    return this.afs.doc<Room>(`rooms/${roomId}`).valueChanges()
  }

  getLive(liveId: string): Rx.Observable<Live> {
    return this.afs.doc<Live>(`lives/${liveId}`).valueChanges()
  }

  getNgwords(magazineId: number): Rx.Observable<MagazineNgWord[]> {
    return this.afs.collection<MagazineNgWord>(`magazines/${magazineId}/ng_words`).valueChanges({idField: "id"})
  }

  getUser(userId: number): Rx.Observable<MagliveUser> {
    return this.afs.doc<MagliveUser>(`users/${userId}`).valueChanges()
  }
  
  getArchiveUploadedLives(magazineId?: number, order?: string, last?: ArchiveUploadLive): Rx.Observable<Live[]> {
    order = order || '-createdAt'
    let orderDirection: OrderByDirection = 'asc'
    if (order.charAt(0) === '-'){
      order = order.substring(1)
      orderDirection = 'desc'
    }
    return this.afs.collection<Live>("lives", ref => {
      let query = ref.where('archiveUploadStatus', 'in', ['INIT', 'PROGRESS','COMPLETED'])
      query = magazineId ? query.where('magazineId', '==', magazineId) : query
      query = query.orderBy(order, orderDirection).limit(INFINITE_SCROLL_PAGE_SIZE)
      return last ? query.startAfter(last.createdAt): query
    })
    .valueChanges()
  }

  getArchiveUploadingLives(): Rx.Observable<Live[]> {
    return this.afs.collection<Live>("lives", ref =>
      ref.where('archiveUploadStatus', '==', 'PROGRESS')
    ).valueChanges()
  }

  loadGifts(lastVisible: Gift, publicStatus: PUBLIC_STATUS, pageSize: number): Rx.Observable<Gift[]> {
    return this.afs.collection<Gift>('gifts', ref => {
      let query = ref.where('publicStatus', '==', publicStatus)
      query = query.orderBy('createdAt', 'desc').orderBy('serialNumber', 'desc').limit(pageSize)
      console.log(`publicStatus: ${publicStatus}`)
      return lastVisible ? query.startAfter(lastVisible.createdAt, lastVisible.serialNumber): query
    })
    .valueChanges();
  }

  getGifts(): Rx.Observable<Gift[]> {
    return this.afs.collection<Gift>(`gifts`, ref => ref.orderBy('displayOrder', 'asc')).valueChanges();
  }

  getGift(giftId: string): Rx.Observable<Gift> {
    return this.afs.doc<Gift>(`gifts/${giftId}`).valueChanges()
  }
}
